import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "./api";

type Row = {
  id: number;
  name: string;
  owner: { id: number; name: string | null; email: string; isActive: boolean };
  createdAt: string;
  state: "trialing" | "trial_expired" | "active" | "past_due" | "past_due_expired" | "canceled" | "none";
  plan: "month" | "year" | null;
  cancelAtPeriodEnd: boolean;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  reviewCount: number;
  managerCount: number;
  /** The linked Google listing, if any. */
  google: { locationId: string; title: string | null } | null;
  daysRemaining: number | null;
  graceDaysLeft: number | null;
  readOnlySince: string | null;
  /** Days until this location is purged; null when it is not on the clock. */
  purgeDaysLeft: number | null;
  /** ENABLE_PURGE is off: the countdown is what WOULD happen. */
  purgePaused: boolean;
};

type Data = {
  summary: {
    users: Record<string, number>;
    // Keyed by state: total, trialing, trialExpired, active, pastDue,
    // readOnly, canceled, none.
    restaurants: Record<string, number>;
    plans: { monthly: number; annual: number };
    mrr: number;
    arr: number;
    expenses: number;
    netMonthly: number;
    pendingSignups: number;
    trialsEndingSoon: number;
  };
  restaurants: Row[];
};

type Expense = {
  id: number;
  name: string;
  amountCents: number;
  active: boolean;
  notes: string | null;
};

const STATE_STYLES: Record<string, string> = {
  active: "bg-teal-100 text-teal-700",
  trialing: "bg-blue-100 text-blue-700",
  trial_expired: "bg-red-100 text-red-700",
  past_due: "bg-amber-100 text-amber-700",
  past_due_expired: "bg-red-100 text-red-700",
  canceled: "bg-gray-200 text-gray-700",
  none: "bg-gray-100 text-gray-500",
};

const Tile: React.FC<{ label: string; value: React.ReactNode; hint?: string; accent?: string }> = ({
  label, value, hint, accent,
}) => (
  <div className="bg-white rounded-xl shadow p-4">
    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${accent || "text-gray-900"}`}>{value}</div>
    {hint && <div className="text-xs text-gray-400 mt-0.5">{hint}</div>}
  </div>
);

/** The state badge, shared by the phone cards and the desktop table. */
const StateBadge: React.FC<{ row: Row }> = ({ row }) => {
  const { t } = useTranslation();
  return (
    <>
      <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${STATE_STYLES[row.state]}`}>
        {t(`billing.state.${row.state === "past_due_expired" ? "trial_expired" : row.state}`, row.state)}
      </span>
      {row.cancelAtPeriodEnd && (
        <div className="text-xs text-amber-600 mt-0.5">{t("master.cancelling")}</div>
      )}
    </>
  );
};

const planLabel = (row: Row) =>
  row.plan === "year" ? "$499/yr" : row.plan === "month" ? "$49.99/mo" : "N/A";

// Setting a trial only makes sense for a restaurant that isn't currently a
// real Stripe subscriber (Fred, 2026-09-18): a paying or lapsed-paying
// customer's access is governed by their subscription, not a trial length.
const canSetTrial = (row: Row) =>
  row.state === "none" || row.state === "trialing" || row.state === "trial_expired";

/** Days left on a trial, days left in the dunning grace window, or renewal date. */
const Timing: React.FC<{ row: Row }> = ({ row }) => {
  const { t } = useTranslation();
  return (
    <>
      {row.daysRemaining !== null && t("master.trialDays", { count: row.daysRemaining })}
      {row.graceDaysLeft !== null && (
        <span className="text-amber-700">{t("master.graceDays", { count: row.graceDaysLeft })}</span>
      )}
      {row.state === "active" && row.currentPeriodEnd &&
        new Date(row.currentPeriodEnd).toLocaleDateString()}
      {/* Red, because this is the one countdown on the page that ends in
          deleting a customer's data; the superadmin extends the trial to stop it. */}
      {row.purgeDaysLeft !== null && (
        <div className="text-red-700 text-xs mt-0.5">
          {t(row.purgePaused ? "master.purgeDaysPaused" : "master.purgeDays", { count: row.purgeDaysLeft })}
        </div>
      )}
    </>
  );
};

const MasterDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState<Row | null>(null);
  const [busy, setBusy] = useState(false);
  const [sweepMsg, setSweepMsg] = useState<string | null>(null);
  const [reminderMsg, setReminderMsg] = useState<string | null>(null);
  const [expenses, setExpenses] = useState<Expense[] | null>(null);
  const [expenseMsg, setExpenseMsg] = useState<string | null>(null);
  const [newExpenseName, setNewExpenseName] = useState("");
  const [newExpenseAmount, setNewExpenseAmount] = useState("");
  const [editingExpenseId, setEditingExpenseId] = useState<number | null>(null);
  const [editExpenseName, setEditExpenseName] = useState("");
  const [editExpenseAmount, setEditExpenseAmount] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await apiFetch("/api/superadmin/master");
      if (!res.ok) { setError(t("master.failedToLoad")); return; }
      setData(await res.json());
    } catch {
      setError(t("master.failedToLoad"));
    }
  }, [t]);

  const loadExpenses = useCallback(async () => {
    try {
      const res = await apiFetch("/api/superadmin/expenses");
      if (!res.ok) { setExpenseMsg(t("master.expensesCard.failedToLoad")); return; }
      const d = await res.json();
      setExpenses(d.expenses);
    } catch {
      setExpenseMsg(t("master.expensesCard.failedToLoad"));
    }
  }, [t]);

  useEffect(() => { load(); loadExpenses(); }, [load, loadExpenses]);

  const addExpense = async () => {
    const amount = parseFloat(newExpenseAmount);
    if (!newExpenseName.trim() || isNaN(amount) || amount < 0) return;
    setBusy(true);
    setExpenseMsg(null);
    try {
      const res = await apiFetch("/api/superadmin/expenses", {
        method: "POST",
        body: JSON.stringify({ name: newExpenseName.trim(), amountCents: Math.round(amount * 100) }),
      });
      if (!res.ok) { const d = await res.json(); setExpenseMsg(d.error || t("master.expensesCard.failedToSave")); return; }
      setNewExpenseName("");
      setNewExpenseAmount("");
      await Promise.all([loadExpenses(), load()]);
    } catch {
      setExpenseMsg(t("master.expensesCard.failedToSave"));
    } finally {
      setBusy(false);
    }
  };

  const toggleExpense = async (exp: Expense) => {
    setBusy(true);
    try {
      const res = await apiFetch(`/api/superadmin/expenses/${exp.id}`, {
        method: "PATCH",
        body: JSON.stringify({ active: !exp.active }),
      });
      if (!res.ok) { const d = await res.json(); setExpenseMsg(d.error || t("master.expensesCard.failedToSave")); return; }
      await Promise.all([loadExpenses(), load()]);
    } finally {
      setBusy(false);
    }
  };

  // Editing a price is the whole point (Fred, 2026-09-23): a vendor raising
  // its rate shouldn't require pushing code, just updating this row.
  const startEditExpense = (exp: Expense) => {
    setEditingExpenseId(exp.id);
    setEditExpenseName(exp.name);
    setEditExpenseAmount((exp.amountCents / 100).toFixed(2));
    setExpenseMsg(null);
  };

  const saveExpenseEdit = async () => {
    if (editingExpenseId === null) return;
    const amount = parseFloat(editExpenseAmount);
    if (!editExpenseName.trim() || isNaN(amount) || amount < 0) return;
    setBusy(true);
    setExpenseMsg(null);
    try {
      const res = await apiFetch(`/api/superadmin/expenses/${editingExpenseId}`, {
        method: "PATCH",
        body: JSON.stringify({ name: editExpenseName.trim(), amountCents: Math.round(amount * 100) }),
      });
      if (!res.ok) { const d = await res.json(); setExpenseMsg(d.error || t("master.expensesCard.failedToSave")); return; }
      setEditingExpenseId(null);
      await Promise.all([loadExpenses(), load()]);
    } finally {
      setBusy(false);
    }
  };

  const deleteExpense = async (exp: Expense) => {
    if (!window.confirm(t("master.expensesCard.confirmDelete", { name: exp.name }))) return;
    setBusy(true);
    try {
      const res = await apiFetch(`/api/superadmin/expenses/${exp.id}`, { method: "DELETE" });
      if (!res.ok) { const d = await res.json(); setExpenseMsg(d.error || t("master.expensesCard.failedToSave")); return; }
      await Promise.all([loadExpenses(), load()]);
    } finally {
      setBusy(false);
    }
  };

  const setTrial = async (row: Row, days: number | null) => {
    // Shortening below "now" removes access immediately, so it gets a confirm.
    if (days !== null && days <= 0 &&
        !window.confirm(t("master.confirmExpire", { name: row.name }))) return;
    setBusy(true);
    try {
      const res = await apiFetch(`/api/superadmin/restaurants/${row.id}/trial`, {
        method: "PATCH",
        body: JSON.stringify(days === null ? { endsAt: null } : { days }),
      });
      if (!res.ok) { const d = await res.json(); setError(d.error || t("master.failedTrial")); return; }
      setEditing(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  // On-demand version of the automated 3-day-before nudge (Fred, 2026-09-18):
  // same email, but the superadmin picks the moment rather than waiting for
  // the sweep's window. Works for any restaurant still trialing.
  const sendTrialReminder = async (row: Row) => {
    setBusy(true);
    setReminderMsg(null);
    try {
      const res = await apiFetch(`/api/superadmin/restaurants/${row.id}/send-trial-reminder`, { method: "POST" });
      if (!res.ok) {
        const d = await res.json();
        setReminderMsg(d.error || t("master.reminderFailed"));
        return;
      }
      setReminderMsg(t("master.reminderSent", { name: row.name }));
      await load();
    } catch {
      setReminderMsg(t("master.reminderFailed"));
    } finally {
      setBusy(false);
    }
  };

  // Support case (G8): a listing linked by the wrong account. The owner is
  // emailed; imported reviews stay.
  const unlinkGoogle = async (row: Row) => {
    if (!row.google) return;
    const title = row.google.title || row.google.locationId;
    if (!window.confirm(t("master.confirmUnlinkGoogle", { name: row.name, title }))) return;
    setBusy(true);
    setSweepMsg(null);
    try {
      const res = await apiFetch(`/api/superadmin/restaurants/${row.id}/google/unlink`, { method: "POST" });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setSweepMsg(d.error || t("master.failedUnlinkGoogle"));
        return;
      }
      setSweepMsg(t("master.unlinkedGoogle", { name: row.name, title }));
      await load();
    } catch {
      setSweepMsg(t("master.failedUnlinkGoogle"));
    } finally {
      setBusy(false);
    }
  };

  const GoogleLine: React.FC<{ row: Row }> = ({ row }) => row.google ? (
    <div className="mt-1 text-xs text-gray-500">
      {t("master.googleListing", { title: row.google.title || row.google.locationId })}{" "}
      <button onClick={() => unlinkGoogle(row)} disabled={busy} className="text-red-700 hover:underline disabled:opacity-50">
        {t("master.unlinkGoogle")}
      </button>
    </div>
  ) : null;

  // The sweep runs on a 09:00 cron. Being able to fire it by hand is what makes
  // a shortened trial testable in one sitting instead of waiting for tomorrow.
  const runDunning = async () => {
    setBusy(true);
    setSweepMsg(null);
    try {
      const res = await apiFetch("/api/superadmin/run-dunning", { method: "POST" });
      setSweepMsg(res.ok ? t("master.sweepDone") : t("master.sweepFailed"));
      if (res.ok) await load();
    } catch {
      setSweepMsg(t("master.sweepFailed"));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="p-8 text-red-600">{error}</div>;
  if (!data) return <div className="p-8 text-gray-500">{t("common.loading")}</div>;

  const { summary } = data;

  // Only the states that actually have restaurants in them. Printing
  // "0 past due · 0 read-only · 0 trial ended" is noise on a wide screen and
  // four wrapped lines on a phone.
  const attentionHint = ([
    ["pastDue", summary.restaurants.pastDue],
    ["readOnly", summary.restaurants.readOnly],
    ["expired", summary.restaurants.trialExpired],
    ["unsubscribed", summary.restaurants.none],
  ] as const)
    .filter(([, n]) => n > 0)
    .map(([key, n]) => t(`master.attn.${key}`, { count: n }))
    .join(" · ") || t("master.attn.allClear");
  const rows = data.restaurants.filter((r) =>
    !filter ||
    r.name.toLowerCase().includes(filter.toLowerCase()) ||
    r.owner.email.toLowerCase().includes(filter.toLowerCase()) ||
    r.state === filter);

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{t("master.title")}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t("master.subtitle")}</p>
        </div>
        <button
          onClick={runDunning}
          disabled={busy}
          title={t("master.runDunningHelp")}
          className="px-3 py-2 border rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
        >
          {t("master.runDunning")}
        </button>
      </div>
      {sweepMsg && <p className="mt-2 text-sm text-teal-700">{sweepMsg}</p>}
      <div className="mb-6" />

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
        {/* MRR is the one number worth reading first, so on a phone it gets the
            full width instead of being squeezed into a half-column. */}
        <div className="col-span-2 lg:col-span-1">
          <Tile label={t("master.mrr")} value={`$${summary.mrr.toFixed(2)}`}
                hint={t("master.arrHint", { arr: summary.arr.toFixed(0) })} accent="text-teal-600" />
        </div>
        <Tile label={t("master.expenses")} value={`$${summary.expenses.toFixed(2)}`}
              hint={t("master.expensesHint")} accent="text-rose-600" />
        <Tile label={t("master.netMonthly")} value={`$${summary.netMonthly.toFixed(2)}`}
              hint={t("master.netMonthlyHint")}
              accent={summary.netMonthly >= 0 ? "text-teal-600" : "text-rose-600"} />
        <Tile label={t("master.paying")} value={summary.restaurants.active}
              hint={t("master.planSplit", { monthly: summary.plans.monthly, annual: summary.plans.annual })} />
        <Tile label={t("master.onTrial")} value={summary.restaurants.trialing}
              hint={t("master.endingSoon", { count: summary.trialsEndingSoon })} accent="text-blue-600" />
        {/* "none" belongs here now. Before the one-trial-per-account rule it was
            a rare edge case; it is now the state every second location starts
            in, so these are read-only locations waiting to be sold - the most
            actionable rows on the page, not a gap in the data. */}
        <Tile label={t("master.attention")}
              value={summary.restaurants.pastDue + summary.restaurants.readOnly
                     + summary.restaurants.trialExpired + summary.restaurants.none}
              hint={attentionHint}
              accent={summary.restaurants.pastDue + summary.restaurants.readOnly
                      + summary.restaurants.none ? "text-amber-600" : undefined} />
        <Tile label={t("master.people")}
              value={(summary.users.admin || 0) + (summary.users.manager || 0)}
              hint={t("master.peopleHint", { admins: summary.users.admin || 0, managers: summary.users.manager || 0 })} />
      </div>

      {/* Fred's own recurring costs (hosting, tools), not tied to Stripe or any
          restaurant -- purely so net revenue above means something. */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        <h2 className="font-semibold text-gray-900">{t("master.expensesCard.title")}</h2>
        {expenseMsg && <p className="text-sm text-rose-600 mt-1">{expenseMsg}</p>}
        {expenses === null ? (
          <p className="text-sm text-gray-400 mt-2">{t("common.loading")}</p>
        ) : expenses.length === 0 ? (
          <p className="text-sm text-gray-400 mt-2">{t("master.expensesCard.empty")}</p>
        ) : (
          <ul className="mt-2 divide-y">
            {expenses.map((e) => (
              <li key={e.id} className="py-2 text-sm">
                {editingExpenseId === e.id ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      className="border rounded p-2 text-sm flex-1 min-w-[10rem]"
                      value={editExpenseName}
                      onChange={(ev) => setEditExpenseName(ev.target.value)}
                    />
                    <input
                      className="border rounded p-2 text-sm w-28"
                      type="number" min="0" step="0.01"
                      value={editExpenseAmount}
                      onChange={(ev) => setEditExpenseAmount(ev.target.value)}
                    />
                    <button disabled={busy || !editExpenseName.trim() || !editExpenseAmount}
                            onClick={saveExpenseEdit}
                            className="px-3 py-2 border border-teal-600 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-50 disabled:opacity-50 whitespace-nowrap">
                      {t("common.save")}
                    </button>
                    <button disabled={busy} onClick={() => setEditingExpenseId(null)}
                            className="text-gray-500 hover:underline whitespace-nowrap">
                      {t("common.cancel")}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className={`min-w-0 ${e.active ? "" : "opacity-50"}`}>
                      <span className="font-medium text-gray-900">{e.name}</span>
                      {e.notes && <span className="text-gray-400 ml-2">{e.notes}</span>}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`tabular-nums ${e.active ? "text-gray-700" : "text-gray-400 line-through"}`}>
                        ${(e.amountCents / 100).toFixed(2)}/mo
                      </span>
                      <button disabled={busy} onClick={() => startEditExpense(e)}
                              className="text-teal-700 hover:underline whitespace-nowrap">
                        {t("common.edit")}
                      </button>
                      <button disabled={busy} onClick={() => toggleExpense(e)}
                              className="text-teal-700 hover:underline whitespace-nowrap">
                        {t(e.active ? "master.expensesCard.deactivate" : "master.expensesCard.activate")}
                      </button>
                      <button disabled={busy} onClick={() => deleteExpense(e)}
                              className="text-rose-700 hover:underline whitespace-nowrap">
                        {t("master.expensesCard.delete")}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
          <input
            className="border rounded p-2 text-sm flex-1 min-w-[10rem]"
            placeholder={t("master.expensesCard.namePlaceholder")}
            value={newExpenseName}
            onChange={(e) => setNewExpenseName(e.target.value)}
          />
          <input
            className="border rounded p-2 text-sm w-32"
            type="number" min="0" step="0.01"
            placeholder={t("master.expensesCard.amountPlaceholder")}
            value={newExpenseAmount}
            onChange={(e) => setNewExpenseAmount(e.target.value)}
          />
          <button
            disabled={busy || !newExpenseName.trim() || !newExpenseAmount}
            onClick={addExpense}
            className="px-3 py-2 border border-teal-600 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-50 disabled:opacity-50"
          >
            {t("master.expensesCard.add")}
          </button>
        </div>
      </div>

      {summary.pendingSignups > 0 && (
        <div className="mb-4 text-sm bg-blue-50 border border-blue-200 text-blue-800 rounded p-3">
          {t("master.pendingSignups", { count: summary.pendingSignups })}
        </div>
      )}

      <input
        className="border rounded p-2 text-sm w-full md:w-80 mb-3"
        placeholder={t("master.filterPlaceholder")}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />

      {/* Phone: a seven-column table at 400px is a horizontal scroll nobody
          reads, so the same rows render as stacked cards. The table below takes
          over from md up. Both drive off the same Row and the same helpers, so
          they cannot drift apart. */}
      <div className="md:hidden space-y-3">
        {rows.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-6 text-center text-gray-500">{t("master.noMatches")}</div>
        ) : rows.map((r) => (
          <div key={r.id} className="bg-white rounded-xl shadow p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-gray-900 break-words">{r.name}</div>
                <div className="text-sm text-gray-600 mt-0.5">{r.owner.name || "N/A"}</div>
                <div className="text-xs text-gray-400 break-all">{r.owner.email}</div>
              </div>
              <div className="shrink-0 text-right"><StateBadge row={r} /></div>
            </div>

            <dl className="grid grid-cols-2 gap-x-3 gap-y-2 mt-3 pt-3 border-t text-sm">
              <div>
                <dt className="text-xs text-gray-400">{t("master.col.plan")}</dt>
                <dd className="text-gray-700">{planLabel(r)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-400">{t("master.col.timing")}</dt>
                <dd className="text-gray-700 tabular-nums"><Timing row={r} /></dd>
              </div>
              <div className="col-span-2">
                <dt className="text-xs text-gray-400">{t("master.col.size")}</dt>
                <dd className="text-gray-700">
                  {t("master.sizeHint", { reviews: r.reviewCount, managers: r.managerCount })}
                  <GoogleLine row={r} />
                </dd>
              </div>
            </dl>

            {canSetTrial(r) && (
              <button
                onClick={() => setEditing(r)}
                className="mt-3 w-full py-2 border border-teal-600 text-teal-700 rounded-lg font-semibold hover:bg-teal-50 transition"
              >
                {t("master.setTrial")}
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="hidden md:block overflow-x-auto bg-white rounded-xl shadow">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              {["restaurant", "owner", "state", "plan", "timing", "size", ""].map((h, i) => (
                <th key={i} className="px-3 py-2 font-medium text-gray-500">{h ? t(`master.col.${h}`) : ""}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">{t("master.noMatches")}</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t align-top">
                <td className="px-3 py-2 font-medium text-gray-900">{r.name}</td>
                <td className="px-3 py-2 text-gray-600">
                  {r.owner.name || "N/A"}<div className="text-xs text-gray-400">{r.owner.email}</div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap"><StateBadge row={r} /></td>
                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{planLabel(r)}</td>
                <td className="px-3 py-2 text-gray-600 whitespace-nowrap tabular-nums">
                  <Timing row={r} />
                </td>
                <td className="px-3 py-2 text-gray-500 text-xs whitespace-nowrap">
                  {t("master.sizeHint", { reviews: r.reviewCount, managers: r.managerCount })}
                  <GoogleLine row={r} />
                </td>
                <td className="px-3 py-2 text-right">
                  {canSetTrial(r) && (
                    <button onClick={() => setEditing(r)} className="text-teal-700 hover:underline text-sm whitespace-nowrap">
                      {t("master.setTrial")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-bold">{t("master.setTrialFor", { name: editing.name })}</h3>
            <p className="text-sm text-gray-600">{t("master.setTrialHelp")}</p>
            <div className="flex flex-wrap gap-2">
              {[1, 10, 15, 30].map((d) => (
                <button key={d} disabled={busy} onClick={() => setTrial(editing, d)}
                        className="px-3 py-2 border rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
                  {t("master.nDays", { count: d })}
                </button>
              ))}
              <button disabled={busy} onClick={() => setTrial(editing, -1)}
                      className="px-3 py-2 border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-50">
                {t("master.expireNow")}
              </button>
            </div>
            {editing.state === "trialing" && (
              <div className="pt-2 border-t">
                <button disabled={busy} onClick={() => sendTrialReminder(editing)}
                        className="w-full px-3 py-2 border border-blue-300 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-50 disabled:opacity-50">
                  {t("master.sendReminderNow")}
                </button>
                <p className="text-xs text-gray-500 mt-1">{t("master.sendReminderHelp")}</p>
              </div>
            )}
            {reminderMsg && <p className="text-sm text-teal-700">{reminderMsg}</p>}
            <div className="flex gap-2 pt-2">
              {/* "Close", not "Cancel": every button above acts immediately on
                  click (there's nothing pending here to cancel), so this is
                  purely dismissing the dialog. */}
              <button onClick={() => { setEditing(null); setReminderMsg(null); }} disabled={busy}
                      className="flex-1 py-2 rounded border font-semibold text-gray-700">
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterDashboard;
